import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  Router,
  Routes,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withHashLocation,
  withRouterConfig,
} from '@angular/router';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { LoadingInterceptor } from '@core/interceptors/loading.interceptor';
import { ServerMessageInterceptor } from '@core/interceptors/server-message.interceptor';
import { ApiPrefixInterceptor } from '@core/interceptors/api-prefix-interceptor';
import { ErrorHandlerInterceptor } from '@core/interceptors/error-handler.interceptor';
import { RefreshTokenInterceptor } from '@core/interceptors/refresh-token-interceptor';
import { AuthService } from '@core/auth/auth.service';
import { jwtOptionsFactory } from '@core/auth/jwt-options.factory';
import { StorageService } from '@core/platform/storage.service';
import { SvgHttpLoaderCustom } from '@core/platform/svg-loader';
import { environment } from '@env';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { version } from '@version';
import { AngularSvgIconModule, SvgLoader } from 'angular-svg-icon';
import { ToastrModule } from 'ngx-toastr';
import { SimplebarAngularModule } from 'simplebar-angular';
import { UI_MODALS_PROVIDERS } from '@shared/ui-modals.providers';
import { RUNS_OWN_ACTIONS_PROVIDER } from '@core/domain/runs-own-actions.provider';
import { LoginViewComponent } from '@features/auth/login/view/login-view.component';
import { APP_ROUTES } from '@app/app.routes';
import localeEn from '@angular/common/locales/en';
import localePl from '@angular/common/locales/pl';

registerLocaleData(localeEn);
registerLocaleData(localePl);

export function createTranslateLoader(http: HttpClient) {
  // Cache-busting: doklejamy wersję aplikacji do URL-a, żeby po deploju
  // przeglądarka/CDN pobrały świeże pliki tłumaczeń zamiast starych z cache.
  return new TranslateHttpLoader(http, `${environment.baseHref}assets/languages/`, `.json?v=${version}`);
}

function svgLoaderFactory(http: HttpClient) {
  return new SvgHttpLoaderCustom(http);
}

export function appInitializerFactory(translate: TranslateService) {
  return () => {
    const savedLang = StorageService.local.getItem('lng') || 'pl';
    translate.setDefaultLang(savedLang);
    return translate.use(savedLang);
  };
}

export class PIMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    return params.key;
  }
}

/**
 * Trasa logowania z dawnego LoginRoutingModule. AppModule importował LoginModule przed AppRoutingModule,
 * więc ta trasa była rejestrowana w głównym routerze jako pierwsza ('/' → ekran logowania). Zachowane 1:1.
 */
const ROOT_LOGIN_ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: LoginViewComponent,
    data: {
      bodyClass: 'gray',
      title: 'Log in',
    },
  },
];

export const appConfig: ApplicationConfig = {
  providers: [
    RUNS_OWN_ACTIONS_PROVIDER,
    // Aplikacja nadal zależy od Zone.js (setTimeout, ręczne CD) — Angular 21 domyślnie startuje zoneless.
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      SimplebarAngularModule,
      BrowserAnimationsModule,
      ToastrModule.forRoot({
        autoDismiss: true,
        preventDuplicates: true,
        positionClass: 'toast-bottom-left',
      }),
      AngularSvgIconModule.forRoot({
        loader: {
          provide: SvgLoader,
          useFactory: svgLoaderFactory,
          deps: [HttpClient],
        },
      }),
      JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useClass: jwtOptionsFactory,
        },
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: PIMissingTranslationHandler,
        },
        isolate: false,
      })
    ),
    // Kolejność jak w dawnym AppModule: router (wraz z inicjalizatorem nawigacji) przed interceptorami i APP_INITIALIZER-ami.
    provideRouter(
      [...ROOT_LOGIN_ROUTES, ...APP_ROUTES],
      withEnabledBlockingInitialNavigation(),
      // Leniwe trasy mają dodatkowy poziom `{ path: '', providers, children }`, więc parametr
      // `:id` projektu nie dochodził do widoków (`route.parent.parent` wskazywał za nisko).
      // Każdy widok widzi teraz parametry wszystkich tras nad sobą w swoim `paramMap`.
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      ...(environment.electron ? [withHashLocation()] : [])
    ),
    // Dawniej z UiModalsModule importowanego w AppModule — instancje w głównym injectorze.
    ...UI_MODALS_PROVIDERS,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    Title,
    { provide: APP_BASE_HREF, useValue: environment.baseHref },
    // Kolejnosc ma znaczenie: adres -> token -> komunikaty serwera -> bledy -> throttling.
    { provide: HTTP_INTERCEPTORS, useClass: ApiPrefixInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RefreshTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ServerMessageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorHandlerInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => {
      const auth = inject(AuthService);
      return new Promise<boolean>((resolve) => {
        auth.initializeUser(resolve);
      });
    }),
    provideAppInitializer(() => appInitializerFactory(inject(TranslateService))()),
    provideHttpClient(withInterceptorsFromDi()),
  ],
};
